import { useEffect, useMemo, useState } from 'react'
import { Box, Button, LinearProgress, Stack, Typography } from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useLocale } from '@/contexts/LocaleContext'
import { teamService, type AccountPackage, type TeamUsage } from '@/services/teamService'
import SectionHeader from '@/components/researcher/SectionHeader'

/** Minutes rendered the way the contract talks about them. */
function formatClip(seconds: number): string {
  const total = Math.max(0, Math.round(seconds))
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

/**
 * The analysis package: how much of it is gone, where it went and what the
 * previous periods cost. Enough for an owner to act before it runs out, and
 * enough to answer an invoice question without opening a database.
 */
export default function UsagePanel({ usage }: { usage: TeamUsage }) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('team')
  const { language } = useLocale()

  const dateFmt = useMemo(
    () => new Intl.DateTimeFormat(language, { day: 'numeric', month: 'long', year: 'numeric' }),
    [language],
  )
  const monthFmt = useMemo(
    () => new Intl.DateTimeFormat(language, { month: 'long' }),
    [language],
  )
  const dayFmt = useMemo(
    () => new Intl.DateTimeFormat(language, { day: 'numeric', month: 'numeric' }),
    [language],
  )

  const percent = usage.percent_used ?? 0
  const barColor = percent >= 100 ? c.coralHover : percent >= 80 ? theme.affexy.emotion.surprise : c.coral
  const unlimited = usage.limit_minutes === null

  return (
    <Box sx={{ bgcolor: c.white, border: `1px solid ${c.ink08}`, borderRadius: '16px', p: 2.5 }}>
      <SectionHeader title={t('usage.title')} />

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        sx={{ alignItems: { sm: 'baseline' }, justifyContent: 'space-between', gap: 1, mt: 2 }}
      >
        <Typography
          sx={{
            fontFamily: theme.affexy.fonts.heading,
            fontSize: 26,
            fontWeight: 700,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {usage.used_minutes}
          <Box component="span" sx={{ fontSize: 14, fontWeight: 400, color: c.ink56, ml: 0.75 }}>
            {unlimited
              ? t('usage.minutesUsed')
              : t('usage.ofLimit', { limit: usage.limit_minutes })}
          </Box>
        </Typography>
        <Typography sx={{ fontSize: 12.5, color: c.ink56, fontFamily: theme.affexy.fonts.body }}>
          {t('usage.renews', { date: dateFmt.format(new Date(usage.period.end)) })}
        </Typography>
      </Stack>

      {!unlimited && (
        <LinearProgress
          variant="determinate"
          value={Math.min(100, percent)}
          sx={{
            mt: 1.5,
            height: 9,
            borderRadius: 999,
            bgcolor: c.gray100,
            '& .MuiLinearProgress-bar': { bgcolor: barColor, borderRadius: 999 },
          }}
        />
      )}

      {usage.is_exhausted && (
        <Box
          sx={{
            mt: 2,
            p: 1.75,
            borderRadius: '12px',
            bgcolor: alpha(c.coral, 0.05),
            border: `1px solid ${alpha(c.coral, 0.25)}`,
            fontSize: 14,
          }}
        >
          {t('usage.exhausted')}
        </Box>
      )}

      <Box
        sx={{
          display: 'grid',
          gap: 2.5,
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          mt: 2.5,
        }}
      >
        <Box>
          <Typography sx={labelSx(c)}>{t('usage.perProject')}</Typography>
          {usage.per_project.length === 0 ? (
            <Typography sx={{ fontSize: 13.5, color: c.ink48 }}>{t('usage.empty')}</Typography>
          ) : (
            <Stack spacing={0.75}>
              {usage.per_project.map((row) => (
                <Row
                  key={`${row.project_id}-${row.name}`}
                  label={row.name || t('usage.deletedStudy')}
                  value={t('usage.minutesShort', { n: row.minutes })}
                />
              ))}
            </Stack>
          )}
        </Box>

        <Box>
          <Typography sx={labelSx(c)}>{t('usage.recent')}</Typography>
          {usage.recent.length === 0 ? (
            <Typography sx={{ fontSize: 13.5, color: c.ink48 }}>{t('usage.empty')}</Typography>
          ) : (
            <Stack spacing={0.75}>
              {usage.recent.map((row, i) => (
                <Row
                  key={i}
                  label={`${dayFmt.format(new Date(row.at))} · ${row.project_name || t('usage.deletedStudy')}`}
                  value={formatClip(row.seconds)}
                />
              ))}
            </Stack>
          )}
        </Box>
      </Box>

      {usage.history.length > 0 && (
        <Box sx={{ mt: 2.5, pt: 2, borderTop: `1px solid ${c.ink08}` }}>
          <Typography sx={labelSx(c)}>{t('usage.history')}</Typography>
          <Stack direction="row" spacing={2.5} sx={{ flexWrap: 'wrap' }}>
            {usage.history.map((row) => (
              <Typography key={row.start} sx={{ fontSize: 13.5, color: c.ink72 }}>
                {monthFmt.format(new Date(row.start))}{' '}
                <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums', color: c.ink }}>
                  {t('usage.minutesShort', { n: row.minutes })}
                </Box>
              </Typography>
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  )
}

/**
 * The same panel for an account that buys respondents instead of minutes.
 *
 * It fetches its own standing so it can be dropped on any screen a FaceWise
 * customer actually reaches — the Team page, where the minutes panel lives, is
 * a room that edition does not have. Minutes stay out of it deliberately: they
 * are our cost unit, not something the customer bought.
 *
 * A package that ran out is never dressed as an error. New responses stop; the
 * results already collected keep opening, and the copy says so.
 */
export function RespondentPackagePanel() {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('team')
  const navigate = useNavigate()
  const [pkg, setPkg] = useState<AccountPackage | null>(null)

  useEffect(() => {
    let cancelled = false
    teamService
      .getAccountPackage()
      .then((data) => !cancelled && setPkg(data))
      .catch(() => !cancelled && setPkg(null))
    return () => {
      cancelled = true
    }
  }, [])

  // Nothing to say until the standing is known — an empty card would only ask
  // the reader to interpret a blank.
  if (!pkg) return null

  const { limit, used, remaining, percent_used, is_exhausted } = pkg.respondents
  const percent = percent_used ?? 0
  // No package on the account at all. In this edition that is not "unlimited"
  // — it is "nothing bought yet", which is also why the pool reads as spent:
  // nobody can start. Saying both at once ("no limit is set" next to "you have
  // used every respondent") is the one thing that must not happen.
  const notActivated = limit === null
  const barColor = percent >= 100 ? c.coralHover : percent >= 80 ? theme.affexy.emotion.surprise : c.coral

  return (
    <Box sx={{ bgcolor: c.white, border: `1px solid ${c.ink08}`, borderRadius: '16px', p: 2.5 }}>
      <SectionHeader title={t('package.title')} />

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        sx={{ alignItems: { sm: 'baseline' }, justifyContent: 'space-between', gap: 1, mt: 2 }}
      >
        <Typography
          sx={{
            fontFamily: theme.affexy.fonts.heading,
            fontSize: 26,
            fontWeight: 700,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {notActivated ? t('package.notActivated') : remaining}
          {!notActivated && (
            <Box component="span" sx={{ fontSize: 14, fontWeight: 400, color: c.ink56, ml: 0.75 }}>
              {t('package.remainingOfLimit', { limit })}
            </Box>
          )}
        </Typography>
        {limit !== null && (
          <Typography sx={{ fontSize: 12.5, color: c.ink56, fontFamily: theme.affexy.fonts.body }}>
            {t('package.used', { used })}
          </Typography>
        )}
      </Stack>

      {limit !== null && (
        <LinearProgress
          variant="determinate"
          value={Math.min(100, percent)}
          sx={{
            mt: 1.5,
            height: 9,
            borderRadius: 999,
            bgcolor: c.gray100,
            '& .MuiLinearProgress-bar': { bgcolor: barColor, borderRadius: 999 },
          }}
        />
      )}

      <Typography sx={{ mt: 1.5, fontSize: 13, color: c.ink56 }}>
        {notActivated ? t('package.notActivatedHint') : t('package.poolHint')}
      </Typography>

      {is_exhausted && !notActivated && (
        <Box
          sx={{
            mt: 2,
            p: 1.75,
            borderRadius: '12px',
            bgcolor: alpha(c.coral, 0.05),
            border: `1px solid ${alpha(c.coral, 0.25)}`,
            fontSize: 14,
          }}
        >
          {t('package.exhausted')}
        </Box>
      )}

      {/* The one action this card has, and it goes to the priced catalogue
          rather than to us: an account with no package still has to choose a
          first one, and an account running low is buying the same thing again.
          Both readings of "more respondents" end on the same screen. */}
      <Button
        onClick={() => navigate('/plans')}
        endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
        sx={{ mt: 2, px: 0, fontWeight: 700, fontSize: 13.5 }}
      >
        {notActivated ? t('package.choose') : t('package.topUp')}
      </Button>
    </Box>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  const theme = useTheme()
  const c = theme.affexy.colors
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, fontSize: 13.5 }}>
      <Box component="span" sx={{ color: c.ink72, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {label}
      </Box>
      <Box component="span" sx={{ color: c.ink56, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
        {value}
      </Box>
    </Box>
  )
}

const labelSx = (c: { ink38?: string; ink48: string }) => ({
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: '.1em',
  textTransform: 'uppercase' as const,
  color: c.ink48,
  mb: 1,
})
